import React, { useState } from 'react';
import {
  Sliders,
  Palette,
  Settings,
  Trash2,
  Copy,
  ChevronLeft,
  Eye,
  EyeOff,
  Upload,
  Plus,
} from 'lucide-react';
import { WIDGET_DEFINITIONS } from './widgets/widgetRegistry';

export default function ElementInspector({
  selectedNode = null,
  onUpdateNode,
  onDuplicateNode,
  onDeleteNode,
  onDeselect,
  onOpenMediaPicker,
}) {
  const [activeTab, setActiveTab] = useState('content'); // content, style, advanced

  if (!selectedNode) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-center text-slate-400 select-none">
        <Sliders className="w-10 h-10 mb-2 opacity-30" />
        <p className="font-semibold text-xs text-slate-600">No element selected</p>
        <p className="text-[11px] mt-1 max-w-xs">
          Click any section, container, or widget in the visual canvas or the layers navigator to configure its settings.
        </p>
      </div>
    );
  }

  const isSection = selectedNode.type === 'section';
  const isContainer = selectedNode.type === 'container';
  const isHeader = selectedNode.type === 'header' || selectedNode.id === 'website-header';
  const isFooter = selectedNode.type === 'footer' || selectedNode.id === 'website-footer';
  const isWidget = !isSection && !isContainer && !isHeader && !isFooter;
  const widgetDef = isWidget ? WIDGET_DEFINITIONS[selectedNode.type] : null;

  // Helper to update specific sub-objects (content, style, responsive, etc.)
  const updateProp = (bucket, field, value) => {
    onUpdateNode({
      ...selectedNode,
      [bucket]: {
        ...(selectedNode[bucket] || {}),
        [field]: value,
      },
    });
  };

  const updateRootProp = (field, value) => {
    onUpdateNode({
      ...selectedNode,
      [field]: value,
    });
  };

  const updateVisibility = (device, isVisible) => {
    onUpdateNode({
      ...selectedNode,
      visibility: {
        ...(selectedNode.visibility || { desktop: true, tablet: true, mobile: true }),
        [device]: isVisible,
      },
    });
  };

  return (
    <div className="flex flex-col h-full bg-white select-none">
      {/* Top Header */}
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center justify-between gap-2 mb-2">
          <button
            type="button"
            onClick={onDeselect}
            className="p-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
            title="Back to Element Library"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 min-w-0">
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#2b3380] bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100">
              {isSection ? 'Section' : isContainer ? 'Container' : isHeader ? 'Website Header & Nav' : isFooter ? 'Website Footer' : widgetDef?.name || selectedNode.type}
            </span>
          </div>
          {!isHeader && !isFooter && (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => onDuplicateNode(selectedNode.id)}
                className="p-1.5 rounded-lg text-slate-500 hover:text-[#2b3380] hover:bg-slate-100 transition-colors"
                title="Duplicate Element"
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => onDeleteNode(selectedNode.id)}
                className="p-1.5 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                title="Delete Element"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>

        {/* Node Name input for Navigator */}
        <input
          type="text"
          disabled={isHeader || isFooter}
          value={isHeader ? 'Website Header & Navigation' : isFooter ? 'Website Footer' : selectedNode.name || ''}
          onChange={(e) => updateRootProp('name', e.target.value)}
          placeholder="Element Label..."
          className="w-full font-bold text-xs text-slate-800 border border-slate-200 rounded-lg px-2.5 py-1 focus:outline-none focus:ring-1 focus:ring-[#2b3380] disabled:bg-slate-50 disabled:text-slate-500"
        />

        {/* Three Tabs: Content | Style | Advanced */}
        <div className="grid grid-cols-3 gap-1 bg-slate-100 p-1 rounded-xl mt-3 text-xs font-semibold">
          <button
            type="button"
            onClick={() => setActiveTab('content')}
            className={`py-1 rounded-lg transition-all flex items-center justify-center gap-1 ${
              activeTab === 'content'
                ? 'bg-white text-[#2b3380] shadow-xs font-bold'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Sliders className="w-3 h-3" />
            <span>Content</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('style')}
            className={`py-1 rounded-lg transition-all flex items-center justify-center gap-1 ${
              activeTab === 'style'
                ? 'bg-white text-[#2b3380] shadow-xs font-bold'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Palette className="w-3 h-3" />
            <span>Style</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('advanced')}
            className={`py-1 rounded-lg transition-all flex items-center justify-center gap-1 ${
              activeTab === 'advanced'
                ? 'bg-white text-[#2b3380] shadow-xs font-bold'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Settings className="w-3 h-3" />
            <span>Advanced</span>
          </button>
        </div>
      </div>

      {/* Tab Panels */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 text-xs">
        {/* ============================================================ */}
        {/* 1. CONTENT TAB                                              */}
        {/* ============================================================ */}
        {activeTab === 'content' && (
          <div className="space-y-4">
            {/* WEBSITE HEADER CONTENT */}
            {isHeader && (
              <div className="space-y-4">
                <div className="p-3 bg-indigo-50/70 border border-indigo-100 rounded-xl text-xs text-indigo-900 leading-relaxed">
                  Configure the storefront header, navigation bar links, branding, and action buttons. Changes apply directly to this page and save with the page document.
                </div>

                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Website / Brand Name</span>
                  <input
                    type="text"
                    value={selectedNode.content?.website_name || ''}
                    onChange={(e) => updateProp('content', 'website_name', e.target.value)}
                    className="w-full border rounded-lg p-2"
                    placeholder="ZOBOX"
                  />
                </label>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-700">Logo Image</span>
                    {onOpenMediaPicker && (
                      <button
                        type="button"
                        onClick={() => onOpenMediaPicker((url) => updateProp('content', 'logo_url', url))}
                        className="text-xs font-semibold text-[#2b3380] hover:underline flex items-center gap-1"
                      >
                        <Upload className="w-3 h-3" /> Media Library
                      </button>
                    )}
                  </div>
                  <input
                    type="text"
                    value={selectedNode.content?.logo_url || ''}
                    onChange={(e) => updateProp('content', 'logo_url', e.target.value)}
                    placeholder="https://... or /brand/logo.png"
                    className="w-full border rounded-lg p-2 font-mono text-[11px]"
                  />
                  {selectedNode.content?.logo_url && (
                    <div className="p-2 border rounded-lg bg-slate-50 flex items-center justify-center">
                      <img
                        src={selectedNode.content.logo_url}
                        alt="Logo preview"
                        className="h-8 max-w-full object-contain"
                      />
                    </div>
                  )}
                </div>

                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Logo Alt Text</span>
                  <input
                    type="text"
                    value={selectedNode.content?.logo_alt || ''}
                    onChange={(e) => updateProp('content', 'logo_alt', e.target.value)}
                    className="w-full border rounded-lg p-2"
                    placeholder="ZOBOX"
                  />
                </label>

                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Logo Link Destination</span>
                  <input
                    type="text"
                    value={selectedNode.content?.logo_href || ''}
                    onChange={(e) => updateProp('content', 'logo_href', e.target.value)}
                    className="w-full border rounded-lg p-2 font-mono text-[11px]"
                    placeholder="/"
                  />
                </label>

                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="font-bold text-slate-700 block mb-1">Support Button</span>
                    <input
                      type="text"
                      value={selectedNode.content?.support_label || ''}
                      onChange={(e) => updateProp('content', 'support_label', e.target.value)}
                      className="w-full border rounded-lg p-2"
                      placeholder="Support"
                    />
                  </label>
                  <label className="block">
                    <span className="font-bold text-slate-700 block mb-1">Support URL</span>
                    <input
                      type="text"
                      value={selectedNode.content?.support_url || ''}
                      onChange={(e) => updateProp('content', 'support_url', e.target.value)}
                      className="w-full border rounded-lg p-2 font-mono text-[11px]"
                      placeholder="/support"
                    />
                  </label>
                </div>

                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Search Placeholder</span>
                  <input
                    type="text"
                    value={selectedNode.content?.search_placeholder || ''}
                    onChange={(e) => updateProp('content', 'search_placeholder', e.target.value)}
                    className="w-full border rounded-lg p-2"
                    placeholder="Search devices..."
                  />
                </label>

                {/* Navigation Items Manager */}
                <div className="space-y-3 pt-2 border-t">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-bold text-xs text-slate-800 block">Navigation Menu Links</span>
                      <span className="text-[10px] text-slate-500">
                        {Array.isArray(selectedNode.content?.nav_items) ? selectedNode.content.nav_items.length : 0} links in primary header
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const current = Array.isArray(selectedNode.content?.nav_items)
                          ? [...selectedNode.content.nav_items]
                          : [];
                        current.push({
                          id: `nav_${Date.now()}`,
                          title: 'New Page',
                          url: '/new-page',
                          enabled: true,
                        });
                        updateProp('content', 'nav_items', current);
                      }}
                      className="px-2.5 py-1 text-xs font-bold text-white bg-[#2b3380] hover:bg-indigo-900 rounded-lg flex items-center gap-1 shadow-xs cursor-pointer"
                    >
                      <Plus className="w-3 h-3" /> Add Link
                    </button>
                  </div>

                  <div className="space-y-2">
                    {(Array.isArray(selectedNode.content?.nav_items) ? selectedNode.content.nav_items : []).map(
                      (item, idx) => (
                        <div
                          key={item.id || idx}
                          className="p-2.5 border rounded-xl bg-slate-50/80 space-y-2"
                        >
                          <div className="flex items-center justify-between gap-1">
                            <input
                              type="text"
                              value={item.title || ''}
                              onChange={(e) => {
                                const current = [...selectedNode.content.nav_items];
                                current[idx] = { ...current[idx], title: e.target.value };
                                updateProp('content', 'nav_items', current);
                              }}
                              className="font-bold text-xs border rounded px-2 py-1 flex-1 bg-white"
                              placeholder="Menu Label"
                            />
                            <div className="flex items-center gap-0.5">
                              <button
                                type="button"
                                disabled={idx === 0}
                                onClick={() => {
                                  const current = [...selectedNode.content.nav_items];
                                  const temp = current[idx];
                                  current[idx] = current[idx - 1];
                                  current[idx - 1] = temp;
                                  updateProp('content', 'nav_items', current);
                                }}
                                className="p-1 hover:bg-slate-200 rounded disabled:opacity-20 text-slate-600"
                                title="Move link up"
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                disabled={idx === selectedNode.content.nav_items.length - 1}
                                onClick={() => {
                                  const current = [...selectedNode.content.nav_items];
                                  const temp = current[idx];
                                  current[idx] = current[idx + 1];
                                  current[idx + 1] = temp;
                                  updateProp('content', 'nav_items', current);
                                }}
                                className="p-1 hover:bg-slate-200 rounded disabled:opacity-20 text-slate-600"
                                title="Move link down"
                              >
                                ↓
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const current = [...selectedNode.content.nav_items];
                                  current.splice(idx, 1);
                                  updateProp('content', 'nav_items', current);
                                }}
                                className="p-1 hover:bg-red-100 text-red-600 rounded"
                                title="Remove link"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={item.url || ''}
                              onChange={(e) => {
                                const current = [...selectedNode.content.nav_items];
                                current[idx] = { ...current[idx], url: e.target.value };
                                updateProp('content', 'nav_items', current);
                              }}
                              className="font-mono text-[11px] border rounded px-2 py-1 flex-1 bg-white text-slate-700"
                              placeholder="/page-url"
                            />
                            <label className="flex items-center gap-1 text-[11px] text-slate-600 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={item.enabled !== false}
                                onChange={(e) => {
                                  const current = [...selectedNode.content.nav_items];
                                  current[idx] = { ...current[idx], enabled: e.target.checked };
                                  updateProp('content', 'nav_items', current);
                                }}
                                className="rounded text-[#2b3380]"
                              />
                              <span>Active</span>
                            </label>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* WEBSITE FOOTER CONTENT */}
            {isFooter && (
              <div className="space-y-4">
                <div className="p-3 bg-slate-100 border border-slate-200 rounded-xl text-xs text-slate-800 leading-relaxed">
                  Configure the storefront footer, action buttons, prompt copy, columns, links, office addresses, and legal copyright.
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-700">Footer Brand Logo</span>
                    {onOpenMediaPicker && (
                      <button
                        type="button"
                        onClick={() => onOpenMediaPicker((url) => updateProp('content', 'brand_logo', url))}
                        className="text-xs font-semibold text-[#2b3380] hover:underline flex items-center gap-1"
                      >
                        <Upload className="w-3 h-3" /> Media Library
                      </button>
                    )}
                  </div>
                  <input
                    type="text"
                    value={selectedNode.content?.brand_logo || ''}
                    onChange={(e) => updateProp('content', 'brand_logo', e.target.value)}
                    placeholder="https://... or /brand/logo.png"
                    className="w-full border rounded-lg p-2 font-mono text-[11px]"
                  />
                </div>

                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Prompt Copy</span>
                  <input
                    type="text"
                    value={selectedNode.content?.prompt || ''}
                    onChange={(e) => updateProp('content', 'prompt', e.target.value)}
                    className="w-full border rounded-lg p-2"
                    placeholder="Got questions? Just ask."
                  />
                </label>

                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Security Disclaimer</span>
                  <textarea
                    rows={2}
                    value={selectedNode.content?.disclaimer || ''}
                    onChange={(e) => updateProp('content', 'disclaimer', e.target.value)}
                    className="w-full border rounded-lg p-2 text-xs"
                    placeholder="ZOBOX employees will never ask for your OTP..."
                  />
                </label>

                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Copyright Notice</span>
                  <input
                    type="text"
                    value={selectedNode.content?.copyright || ''}
                    onChange={(e) => updateProp('content', 'copyright', e.target.value)}
                    className="w-full border rounded-lg p-2 text-xs"
                    placeholder="© {year} Zobox Protech Limited. All rights reserved."
                  />
                </label>

                {/* Footer Columns Manager */}
                <div className="space-y-3 pt-2 border-t">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-bold text-xs text-slate-800 block">Footer Navigation Columns</span>
                      <span className="text-[10px] text-slate-500">
                        {Array.isArray(selectedNode.content?.columns) ? selectedNode.content.columns.length : 0} columns
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const current = Array.isArray(selectedNode.content?.columns)
                          ? [...selectedNode.content.columns]
                          : [];
                        current.push({
                          id: `col_${Date.now()}`,
                          title: 'New Column',
                          links: [
                            { id: `l_${Date.now()}`, label: 'Link 1', href: '/' }
                          ],
                        });
                        updateProp('content', 'columns', current);
                      }}
                      className="px-2.5 py-1 text-xs font-bold text-white bg-[#2b3380] hover:bg-indigo-900 rounded-lg flex items-center gap-1 shadow-xs cursor-pointer"
                    >
                      <Plus className="w-3 h-3" /> Add Column
                    </button>
                  </div>

                  <div className="space-y-3">
                    {(Array.isArray(selectedNode.content?.columns) ? selectedNode.content.columns : []).map(
                      (col, cIdx) => (
                        <div key={col.id || cIdx} className="p-3 border rounded-xl bg-slate-50 space-y-2">
                          <div className="flex items-center justify-between gap-1">
                            <input
                              type="text"
                              value={col.title || ''}
                              onChange={(e) => {
                                const current = [...selectedNode.content.columns];
                                current[cIdx] = { ...current[cIdx], title: e.target.value };
                                updateProp('content', 'columns', current);
                              }}
                              className="font-bold text-xs border rounded px-2 py-1 flex-1 bg-white"
                              placeholder="Column Title"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const current = [...selectedNode.content.columns];
                                current.splice(cIdx, 1);
                                updateProp('content', 'columns', current);
                              }}
                              className="p-1 hover:bg-red-100 text-red-600 rounded"
                              title="Delete Column"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>

                          {/* Column Links */}
                          <div className="space-y-1.5 pl-2 border-l-2 border-indigo-200">
                            {(col.links || []).map((link, lIdx) => (
                              <div key={link.id || lIdx} className="flex items-center gap-1">
                                <input
                                  type="text"
                                  value={link.label || ''}
                                  onChange={(e) => {
                                    const current = [...selectedNode.content.columns];
                                    const links = [...(current[cIdx].links || [])];
                                    links[lIdx] = { ...links[lIdx], label: e.target.value };
                                    current[cIdx] = { ...current[cIdx], links };
                                    updateProp('content', 'columns', current);
                                  }}
                                  placeholder="Link text"
                                  className="border rounded px-1.5 py-0.5 text-xs flex-1 bg-white"
                                />
                                <input
                                  type="text"
                                  value={link.href || ''}
                                  onChange={(e) => {
                                    const current = [...selectedNode.content.columns];
                                    const links = [...(current[cIdx].links || [])];
                                    links[lIdx] = { ...links[lIdx], href: e.target.value };
                                    current[cIdx] = { ...current[cIdx], links };
                                    updateProp('content', 'columns', current);
                                  }}
                                  placeholder="/url"
                                  className="border rounded px-1.5 py-0.5 text-[11px] font-mono flex-1 bg-white"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const current = [...selectedNode.content.columns];
                                    const links = [...(current[cIdx].links || [])];
                                    links.splice(lIdx, 1);
                                    current[cIdx] = { ...current[cIdx], links };
                                    updateProp('content', 'columns', current);
                                  }}
                                  className="text-red-500 hover:text-red-700 px-1"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => {
                                const current = [...selectedNode.content.columns];
                                const links = [...(current[cIdx].links || [])];
                                links.push({ id: `l_${Date.now()}`, label: 'New Link', href: '/' });
                                current[cIdx] = { ...current[cIdx], links };
                                updateProp('content', 'columns', current);
                              }}
                              className="text-[11px] text-[#2b3380] hover:underline font-semibold flex items-center gap-0.5 pt-1 cursor-pointer"
                            >
                              <Plus className="w-3 h-3" /> Add Link
                            </button>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* SECTION CONTENT */}
            {isSection && (
              <div className="space-y-3">
                <p className="text-slate-500 text-[11px]">
                  Sections form full-width horizontal rows across the page.
                </p>
              </div>
            )}

            {/* CONTAINER CONTENT */}
            {isContainer && (
              <div className="space-y-3">
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Column Layout</span>
                  <select
                    value={selectedNode.layout || '1-column'}
                    onChange={(e) => updateRootProp('layout', e.target.value)}
                    className="w-full border rounded-lg p-2 bg-white"
                  >
                    <option value="1-column">1 Full Column (100%)</option>
                    <option value="2-column">2 Equal Columns (50% / 50%)</option>
                    <option value="3-column">3 Equal Columns (33% / 33% / 33%)</option>
                    <option value="4-column">4 Equal Columns (25% / 25% / 25% / 25%)</option>
                    <option value="sidebar-left">Sidebar Left (30% / 70%)</option>
                    <option value="sidebar-right">Sidebar Right (70% / 30%)</option>
                  </select>
                </label>
              </div>
            )}

            {/* HEADING WIDGET */}
            {selectedNode.type === 'heading' && (
              <div className="space-y-3">
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Heading Text</span>
                  <input
                    type="text"
                    value={selectedNode.content?.text || ''}
                    onChange={(e) => updateProp('content', 'text', e.target.value)}
                    className="w-full border rounded-lg p-2"
                  />
                </label>
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">HTML Heading Tag</span>
                  <select
                    value={selectedNode.content?.level || 'h2'}
                    onChange={(e) => updateProp('content', 'level', e.target.value)}
                    className="w-full border rounded-lg p-2 bg-white font-mono"
                  >
                    <option value="h1">H1 (Main Page Title)</option>
                    <option value="h2">H2 (Major Section)</option>
                    <option value="h3">H3 (Subsection)</option>
                    <option value="h4">H4 (Card Headline)</option>
                    <option value="h5">H5 (Minor Tag)</option>
                  </select>
                </label>
              </div>
            )}

            {/* TEXT WIDGET */}
            {selectedNode.type === 'text' && (
              <div className="space-y-3">
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Content (Markdown / Text)</span>
                  <textarea
                    rows={6}
                    value={selectedNode.content?.content || ''}
                    onChange={(e) => updateProp('content', 'content', e.target.value)}
                    placeholder="Enter paragraphs or markdown..."
                    className="w-full border rounded-lg p-2 leading-relaxed"
                  />
                  <span className="text-[10px] text-slate-400 block mt-1">
                    Blank lines separate paragraphs; # makes heading; - makes bullet items.
                  </span>
                </label>
              </div>
            )}

            {/* IMAGE WIDGET */}
            {selectedNode.type === 'image' && (
              <div className="space-y-3">
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Image URL</span>
                  <span className="text-[10px] font-medium text-indigo-700 block mb-1.5">Recommended: 1200 × 675 px (16:9), max 5 MB</span>
                  <div className="flex gap-1.5">
                    <input
                      type="text"
                      value={selectedNode.content?.url || ''}
                      onChange={(e) => updateProp('content', 'url', e.target.value)}
                      placeholder="/uploads/cms/... or https://..."
                      className="flex-1 border rounded-lg p-2 font-mono text-[11px]"
                    />
                    {onOpenMediaPicker && (
                      <button
                        type="button"
                        onClick={() =>
                          onOpenMediaPicker((url) => updateProp('content', 'url', url))
                        }
                        className="px-2.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700 flex items-center gap-1 font-semibold"
                        title="Browse Media Library"
                      >
                        <Upload className="w-3.5 h-3.5" />
                        <span>Media</span>
                      </button>
                    )}
                  </div>
                </label>
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Alt Text (Accessibility)</span>
                  <input
                    type="text"
                    value={selectedNode.content?.alt || ''}
                    onChange={(e) => updateProp('content', 'alt', e.target.value)}
                    placeholder="Describe image for SEO and screen readers"
                    className="w-full border rounded-lg p-2"
                  />
                </label>
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Caption (Optional)</span>
                  <input
                    type="text"
                    value={selectedNode.content?.caption || ''}
                    onChange={(e) => updateProp('content', 'caption', e.target.value)}
                    className="w-full border rounded-lg p-2"
                  />
                </label>
              </div>
            )}

            {/* BUTTON WIDGET */}
            {selectedNode.type === 'button' && (
              <div className="space-y-3">
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Button Label</span>
                  <input
                    type="text"
                    value={selectedNode.content?.label || ''}
                    onChange={(e) => updateProp('content', 'label', e.target.value)}
                    className="w-full border rounded-lg p-2"
                  />
                </label>
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Destination URL</span>
                  <input
                    type="text"
                    value={selectedNode.content?.url || ''}
                    onChange={(e) => updateProp('content', 'url', e.target.value)}
                    placeholder="/buy or https://..."
                    className="w-full border rounded-lg p-2"
                  />
                </label>
                <label className="flex items-center gap-2 cursor-pointer pt-1">
                  <input
                    type="checkbox"
                    checked={Boolean(selectedNode.content?.external)}
                    onChange={(e) => updateProp('content', 'external', e.target.checked)}
                    className="rounded text-[#2b3380]"
                  />
                  <span className="text-slate-700 font-medium">Open in new tab / External link</span>
                </label>
              </div>
            )}

            {/* PAGE HERO WIDGET */}
            {selectedNode.type === 'page_hero' && (
              <div className="space-y-3">
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Eyebrow / Small Tag</span>
                  <input
                    type="text"
                    value={selectedNode.content?.eyebrow || ''}
                    onChange={(e) => updateProp('content', 'eyebrow', e.target.value)}
                    className="w-full border rounded-lg p-2"
                  />
                </label>
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Hero Title</span>
                  <input
                    type="text"
                    value={selectedNode.content?.title || ''}
                    onChange={(e) => updateProp('content', 'title', e.target.value)}
                    className="w-full border rounded-lg p-2"
                  />
                </label>
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Excerpt / Intro</span>
                  <textarea
                    rows={3}
                    value={selectedNode.content?.excerpt || ''}
                    onChange={(e) => updateProp('content', 'excerpt', e.target.value)}
                    className="w-full border rounded-lg p-2"
                  />
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="font-bold text-slate-700 block mb-1">Button Text</span>
                    <input
                      type="text"
                      value={selectedNode.content?.cta_text || ''}
                      onChange={(e) => updateProp('content', 'cta_text', e.target.value)}
                      className="w-full border rounded-lg p-2"
                    />
                  </label>
                  <label className="block">
                    <span className="font-bold text-slate-700 block mb-1">Button URL</span>
                    <input
                      type="text"
                      value={selectedNode.content?.cta_url || ''}
                      onChange={(e) => updateProp('content', 'cta_url', e.target.value)}
                      className="w-full border rounded-lg p-2"
                    />
                  </label>
                </div>
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Layout Preset</span>
                  <select
                    value={selectedNode.content?.layout || 'wave'}
                    onChange={(e) => updateProp('content', 'layout', e.target.value)}
                    className="w-full border rounded-lg p-2 bg-white"
                  >
                    <option value="wave">Wave (Light Card with Eyebrow)</option>
                    <option value="gateway">Gateway (Dark Blue Gradient)</option>
                    <option value="content">Content (Clean Editorial)</option>
                  </select>
                </label>
              </div>
            )}

            {/* CARD WIDGET */}
            {selectedNode.type === 'card' && (
              <div className="space-y-3">
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Card Title</span>
                  <input
                    type="text"
                    value={selectedNode.content?.title || ''}
                    onChange={(e) => updateProp('content', 'title', e.target.value)}
                    className="w-full border rounded-lg p-2"
                  />
                </label>
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Eyebrow</span>
                  <input
                    type="text"
                    value={selectedNode.content?.eyebrow || ''}
                    onChange={(e) => updateProp('content', 'eyebrow', e.target.value)}
                    className="w-full border rounded-lg p-2"
                  />
                </label>
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Description</span>
                  <textarea
                    rows={2}
                    value={selectedNode.content?.description || ''}
                    onChange={(e) => updateProp('content', 'description', e.target.value)}
                    className="w-full border rounded-lg p-2"
                  />
                </label>
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Image URL</span>
                  <span className="text-[10px] font-medium text-indigo-700 block mb-1.5">Recommended: 800 × 600 px (4:3), max 5 MB</span>
                  <div className="flex gap-1.5">
                    <input
                      type="text"
                      value={selectedNode.content?.image || ''}
                      onChange={(e) => updateProp('content', 'image', e.target.value)}
                      className="flex-1 border rounded-lg p-2 font-mono text-[11px]"
                    />
                    {onOpenMediaPicker && (
                      <button
                        type="button"
                        onClick={() =>
                          onOpenMediaPicker((url) => updateProp('content', 'image', url))
                        }
                        className="px-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700 flex items-center gap-1 font-semibold"
                      >
                        <Upload className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="font-bold text-slate-700 block mb-1">Link URL</span>
                    <input
                      type="text"
                      value={selectedNode.content?.href || ''}
                      onChange={(e) => updateProp('content', 'href', e.target.value)}
                      className="w-full border rounded-lg p-2"
                    />
                  </label>
                  <label className="block">
                    <span className="font-bold text-slate-700 block mb-1">Button Label</span>
                    <input
                      type="text"
                      value={selectedNode.content?.cta_label || ''}
                      onChange={(e) => updateProp('content', 'cta_label', e.target.value)}
                      className="w-full border rounded-lg p-2"
                    />
                  </label>
                </div>
              </div>
            )}

            {/* PRODUCT RAIL WIDGET */}
            {selectedNode.type === 'product_rail' && (
              <div className="space-y-3">
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Rail Heading</span>
                  <input
                    type="text"
                    value={selectedNode.content?.title || ''}
                    onChange={(e) => updateProp('content', 'title', e.target.value)}
                    className="w-full border rounded-lg p-2"
                  />
                </label>
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Filter by Category</span>
                  <select
                    value={selectedNode.content?.category || 'Mobile'}
                    onChange={(e) => updateProp('content', 'category', e.target.value)}
                    className="w-full border rounded-lg p-2 bg-white"
                  >
                    <option value="">All Categories</option>
                    <option value="Mobile">Mobile (Smartphones)</option>
                    <option value="Laptop">Laptop (Laptops & MacBooks)</option>
                  </select>
                </label>
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Number of Products</span>
                  <input
                    type="number"
                    min="1"
                    max="12"
                    value={selectedNode.content?.limit || 4}
                    onChange={(e) => updateProp('content', 'limit', Number(e.target.value))}
                    className="w-full border rounded-lg p-2"
                  />
                </label>
              </div>
            )}

            {/* FEATURE GRID WIDGET */}
            {selectedNode.type === 'feature_grid' && (
              <div className="space-y-4">
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Section Title</span>
                  <input
                    type="text"
                    value={selectedNode.content?.title || ''}
                    onChange={(e) => updateProp('content', 'title', e.target.value)}
                    className="w-full border rounded-lg p-2"
                    placeholder="Why Choose Zobiz Retailer App?..."
                  />
                </label>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-xs text-slate-700">Features List ({Array.isArray(selectedNode.content?.features) ? selectedNode.content.features.length : 0})</span>
                    <button
                      type="button"
                      onClick={() => {
                        const current = Array.isArray(selectedNode.content?.features) ? [...selectedNode.content.features] : [];
                        current.push({
                          icon: '/svgs/becomePartner/warranty.svg',
                          title: 'New Feature',
                          description: 'Feature description details',
                        });
                        updateProp('content', 'features', current);
                      }}
                      className="text-xs font-bold text-[#2b3380] hover:underline flex items-center gap-1"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Feature
                    </button>
                  </div>
                  {(Array.isArray(selectedNode.content?.features) ? selectedNode.content.features : []).map((feat, fIdx) => (
                    <div key={fIdx} className="p-3 border rounded-xl bg-slate-50 space-y-2 relative">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold text-slate-500">#{fIdx + 1} Feature</span>
                        <button
                          type="button"
                          onClick={() => {
                            const current = [...selectedNode.content.features];
                            current.splice(fIdx, 1);
                            updateProp('content', 'features', current);
                          }}
                          className="text-red-500 hover:text-red-700 p-1"
                          title="Remove Feature"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-slate-600 block mb-1">Feature Title</span>
                        <input
                          type="text"
                          value={feat.title || ''}
                          onChange={(e) => {
                            const current = [...selectedNode.content.features];
                            current[fIdx] = { ...current[fIdx], title: e.target.value };
                            updateProp('content', 'features', current);
                          }}
                          className="w-full border rounded-lg p-1.5 text-xs bg-white"
                        />
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-slate-600 block mb-1">Icon URL</span>
                        <div className="flex gap-1">
                          <input
                            type="text"
                            value={feat.icon || ''}
                            onChange={(e) => {
                              const current = [...selectedNode.content.features];
                              current[fIdx] = { ...current[fIdx], icon: e.target.value };
                              updateProp('content', 'features', current);
                            }}
                            className="flex-1 border rounded-lg p-1.5 text-[11px] font-mono bg-white"
                          />
                          {onOpenMediaPicker && (
                            <button
                              type="button"
                              onClick={() =>
                                onOpenMediaPicker((url) => {
                                  const current = [...selectedNode.content.features];
                                  current[fIdx] = { ...current[fIdx], icon: url };
                                  updateProp('content', 'features', current);
                                })
                              }
                              className="px-2 bg-slate-200 hover:bg-slate-300 rounded text-slate-700"
                            >
                              <Upload className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-slate-600 block mb-1">Description</span>
                        <textarea
                          rows={2}
                          value={feat.description || ''}
                          onChange={(e) => {
                            const current = [...selectedNode.content.features];
                            current[fIdx] = { ...current[fIdx], description: e.target.value };
                            updateProp('content', 'features', current);
                          }}
                          className="w-full border rounded-lg p-1.5 text-xs bg-white"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* STEP PROCESS WIDGET */}
            {selectedNode.type === 'step_process' && (
              <div className="space-y-4">
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Section Title</span>
                  <input
                    type="text"
                    value={selectedNode.content?.title || ''}
                    onChange={(e) => updateProp('content', 'title', e.target.value)}
                    className="w-full border rounded-lg p-2"
                    placeholder="How to Partner with Zobiz..."
                  />
                </label>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-xs text-slate-700">Steps ({Array.isArray(selectedNode.content?.steps) ? selectedNode.content.steps.length : 0})</span>
                    <button
                      type="button"
                      onClick={() => {
                        const current = Array.isArray(selectedNode.content?.steps) ? [...selectedNode.content.steps] : [];
                        current.push({
                          step: String(current.length + 1),
                          title: `Step ${current.length + 1}`,
                          desc: 'Step explanation details',
                          img: '/svgs/becomePartner/step1.png',
                        });
                        updateProp('content', 'steps', current);
                      }}
                      className="text-xs font-bold text-[#2b3380] hover:underline flex items-center gap-1"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Step
                    </button>
                  </div>
                  {(Array.isArray(selectedNode.content?.steps) ? selectedNode.content.steps : []).map((st, sIdx) => (
                    <div key={sIdx} className="p-3 border rounded-xl bg-slate-50 space-y-2 relative">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold text-slate-500">Step {sIdx + 1}</span>
                        <button
                          type="button"
                          onClick={() => {
                            const current = [...selectedNode.content.steps];
                            current.splice(sIdx, 1);
                            updateProp('content', 'steps', current);
                          }}
                          className="text-red-500 hover:text-red-700 p-1"
                          title="Remove Step"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-slate-600 block mb-1">Step Title</span>
                        <input
                          type="text"
                          value={st.title || ''}
                          onChange={(e) => {
                            const current = [...selectedNode.content.steps];
                            current[sIdx] = { ...current[sIdx], title: e.target.value };
                            updateProp('content', 'steps', current);
                          }}
                          className="w-full border rounded-lg p-1.5 text-xs bg-white"
                        />
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-slate-600 block mb-1">Graphic / Image URL</span>
                        <div className="flex gap-1">
                          <input
                            type="text"
                            value={st.img || ''}
                            onChange={(e) => {
                              const current = [...selectedNode.content.steps];
                              current[sIdx] = { ...current[sIdx], img: e.target.value };
                              updateProp('content', 'steps', current);
                            }}
                            className="flex-1 border rounded-lg p-1.5 text-[11px] font-mono bg-white"
                          />
                          {onOpenMediaPicker && (
                            <button
                              type="button"
                              onClick={() =>
                                onOpenMediaPicker((url) => {
                                  const current = [...selectedNode.content.steps];
                                  current[sIdx] = { ...current[sIdx], img: url };
                                  updateProp('content', 'steps', current);
                                })
                              }
                              className="px-2 bg-slate-200 hover:bg-slate-300 rounded text-slate-700"
                            >
                              <Upload className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-slate-600 block mb-1">Description</span>
                        <textarea
                          rows={2}
                          value={st.desc || st.description || ''}
                          onChange={(e) => {
                            const current = [...selectedNode.content.steps];
                            current[sIdx] = { ...current[sIdx], desc: e.target.value, description: e.target.value };
                            updateProp('content', 'steps', current);
                          }}
                          className="w-full border rounded-lg p-1.5 text-xs bg-white"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* MEDIA BANNER WIDGET */}
            {selectedNode.type === 'media_banner' && (
              <div className="space-y-3">
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Banner Title / Caption</span>
                  <input
                    type="text"
                    value={selectedNode.content?.title || ''}
                    onChange={(e) => updateProp('content', 'title', e.target.value)}
                    className="w-full border rounded-lg p-2"
                  />
                </label>
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Banner Image URL</span>
                  <div className="flex gap-1.5">
                    <input
                      type="text"
                      value={selectedNode.content?.image || selectedNode.content?.url || ''}
                      onChange={(e) => {
                        updateProp('content', 'image', e.target.value);
                        updateProp('content', 'url', e.target.value);
                      }}
                      className="flex-1 border rounded-lg p-2 font-mono text-[11px]"
                    />
                    {onOpenMediaPicker && (
                      <button
                        type="button"
                        onClick={() =>
                          onOpenMediaPicker((url) => {
                            updateProp('content', 'image', url);
                            updateProp('content', 'url', url);
                          })
                        }
                        className="px-2.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700 flex items-center gap-1 font-semibold"
                      >
                        <Upload className="w-3.5 h-3.5" />
                        <span>Media</span>
                      </button>
                    )}
                  </div>
                </label>
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Alt Text (Accessibility & SEO)</span>
                  <input
                    type="text"
                    value={selectedNode.content?.alt || ''}
                    onChange={(e) => updateProp('content', 'alt', e.target.value)}
                    className="w-full border rounded-lg p-2"
                  />
                </label>
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Link URL (Optional)</span>
                  <input
                    type="text"
                    value={selectedNode.content?.link || selectedNode.content?.href || ''}
                    onChange={(e) => {
                      updateProp('content', 'link', e.target.value);
                      updateProp('content', 'href', e.target.value);
                    }}
                    placeholder="/buy or https://..."
                    className="w-full border rounded-lg p-2"
                  />
                </label>
              </div>
            )}

            {/* VIDEO PLAYER WIDGET */}
            {selectedNode.type === 'video_player' && (
              <div className="space-y-3">
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Video Title</span>
                  <input
                    type="text"
                    value={selectedNode.content?.title || ''}
                    onChange={(e) => updateProp('content', 'title', e.target.value)}
                    className="w-full border rounded-lg p-2"
                  />
                </label>
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Video URL (MP4 or YouTube Embed)</span>
                  <input
                    type="text"
                    value={selectedNode.content?.url || ''}
                    onChange={(e) => updateProp('content', 'url', e.target.value)}
                    placeholder="https://www.youtube.com/embed/... or /videos/sample.mp4"
                    className="w-full border rounded-lg p-2 font-mono text-[11px]"
                  />
                </label>
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Poster Thumbnail URL</span>
                  <div className="flex gap-1.5">
                    <input
                      type="text"
                      value={selectedNode.content?.poster || ''}
                      onChange={(e) => updateProp('content', 'poster', e.target.value)}
                      className="flex-1 border rounded-lg p-2 font-mono text-[11px]"
                    />
                    {onOpenMediaPicker && (
                      <button
                        type="button"
                        onClick={() =>
                          onOpenMediaPicker((url) => updateProp('content', 'poster', url))
                        }
                        className="px-2.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700 flex items-center gap-1 font-semibold"
                      >
                        <Upload className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </label>
              </div>
            )}

            {/* CONTACT CHANNELS WIDGET */}
            {selectedNode.type === 'contact_channels' && (
              <div className="space-y-3">
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">WhatsApp URL or Number</span>
                  <input
                    type="text"
                    value={selectedNode.content?.whatsapp || ''}
                    onChange={(e) => updateProp('content', 'whatsapp', e.target.value)}
                    placeholder="https://wa.me/919873788964"
                    className="w-full border rounded-lg p-2"
                  />
                </label>
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Phone Number</span>
                  <input
                    type="text"
                    value={selectedNode.content?.phone || ''}
                    onChange={(e) => updateProp('content', 'phone', e.target.value)}
                    placeholder="9873788964"
                    className="w-full border rounded-lg p-2"
                  />
                </label>
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Email Address</span>
                  <input
                    type="email"
                    value={selectedNode.content?.email || ''}
                    onChange={(e) => updateProp('content', 'email', e.target.value)}
                    placeholder="support@zobox.in"
                    className="w-full border rounded-lg p-2"
                  />
                </label>
              </div>
            )}

            {/* REACH US BOXES WIDGET */}
            {selectedNode.type === 'reach_us_boxes' && (
              <div className="space-y-4">
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Section Title</span>
                  <input
                    type="text"
                    value={selectedNode.content?.title || ''}
                    onChange={(e) => updateProp('content', 'title', e.target.value)}
                    className="w-full border rounded-lg p-2"
                    placeholder="Reach Us"
                  />
                </label>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-xs text-slate-700">Location / Contact Boxes ({Array.isArray(selectedNode.content?.boxes) ? selectedNode.content.boxes.length : 0})</span>
                    <button
                      type="button"
                      onClick={() => {
                        const current = Array.isArray(selectedNode.content?.boxes) ? [...selectedNode.content.boxes] : [];
                        current.push({
                          icon: '📍',
                          title: 'New Office Location',
                          text: 'Address or contact details here',
                        });
                        updateProp('content', 'boxes', current);
                      }}
                      className="text-xs font-bold text-[#2b3380] hover:underline flex items-center gap-1"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Box
                    </button>
                  </div>
                  {(Array.isArray(selectedNode.content?.boxes) ? selectedNode.content.boxes : []).map((b, bIdx) => (
                    <div key={bIdx} className="p-3 border rounded-xl bg-slate-50 space-y-2 relative">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold text-slate-500">Box #{bIdx + 1}</span>
                        <button
                          type="button"
                          onClick={() => {
                            const current = [...selectedNode.content.boxes];
                            current.splice(bIdx, 1);
                            updateProp('content', 'boxes', current);
                          }}
                          className="text-red-500 hover:text-red-700 p-1"
                          title="Remove Box"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-slate-600 block mb-1">Box Title</span>
                        <input
                          type="text"
                          value={b.title || ''}
                          onChange={(e) => {
                            const current = [...selectedNode.content.boxes];
                            current[bIdx] = { ...current[bIdx], title: e.target.value };
                            updateProp('content', 'boxes', current);
                          }}
                          className="w-full border rounded-lg p-1.5 text-xs bg-white"
                        />
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-slate-600 block mb-1">Details / Address</span>
                        <textarea
                          rows={2}
                          value={b.text || ''}
                          onChange={(e) => {
                            const current = [...selectedNode.content.boxes];
                            current[bIdx] = { ...current[bIdx], text: e.target.value };
                            updateProp('content', 'boxes', current);
                          }}
                          className="w-full border rounded-lg p-1.5 text-xs bg-white"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* GRADE TABS WIDGET */}
            {selectedNode.type === 'grade_tabs' && (
              <div className="space-y-4">
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Section Title</span>
                  <input
                    type="text"
                    value={selectedNode.content?.title || ''}
                    onChange={(e) => updateProp('content', 'title', e.target.value)}
                    className="w-full border rounded-lg p-2"
                    placeholder="Our Grades Explanations"
                  />
                </label>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-xs text-slate-700">Grading Tabs ({Array.isArray(selectedNode.content?.tabs) ? selectedNode.content.tabs.length : 0})</span>
                    <button
                      type="button"
                      onClick={() => {
                        const current = Array.isArray(selectedNode.content?.tabs) ? [...selectedNode.content.tabs] : [];
                        current.push({
                          name: 'New Grade',
                          points: ['100% Fully Functional', '12 Month Warranty'],
                          displayText: 'Screen condition details',
                          physicalText: 'Body condition details',
                        });
                        updateProp('content', 'tabs', current);
                      }}
                      className="text-xs font-bold text-[#2b3380] hover:underline flex items-center gap-1"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Grade Tab
                    </button>
                  </div>
                  {(Array.isArray(selectedNode.content?.tabs) ? selectedNode.content.tabs : []).map((tab, tIdx) => (
                    <div key={tIdx} className="p-3 border rounded-xl bg-slate-50 space-y-2 relative">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold text-slate-500">Grade: {tab.name}</span>
                        <button
                          type="button"
                          onClick={() => {
                            const current = [...selectedNode.content.tabs];
                            current.splice(tIdx, 1);
                            updateProp('content', 'tabs', current);
                          }}
                          className="text-red-500 hover:text-red-700 p-1"
                          title="Remove Tab"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-slate-600 block mb-1">Grade Name</span>
                        <input
                          type="text"
                          value={tab.name || ''}
                          onChange={(e) => {
                            const current = [...selectedNode.content.tabs];
                            current[tIdx] = { ...current[tIdx], name: e.target.value };
                            updateProp('content', 'tabs', current);
                          }}
                          className="w-full border rounded-lg p-1.5 text-xs bg-white"
                        />
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-slate-600 block mb-1">Display Condition</span>
                        <input
                          type="text"
                          value={tab.displayText || ''}
                          onChange={(e) => {
                            const current = [...selectedNode.content.tabs];
                            current[tIdx] = { ...current[tIdx], displayText: e.target.value };
                            updateProp('content', 'tabs', current);
                          }}
                          className="w-full border rounded-lg p-1.5 text-xs bg-white"
                        />
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-slate-600 block mb-1">Physical / Body Condition</span>
                        <input
                          type="text"
                          value={tab.physicalText || ''}
                          onChange={(e) => {
                            const current = [...selectedNode.content.tabs];
                            current[tIdx] = { ...current[tIdx], physicalText: e.target.value };
                            updateProp('content', 'tabs', current);
                          }}
                          className="w-full border rounded-lg p-1.5 text-xs bg-white"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* FAQ ACCORDION WIDGET */}
            {selectedNode.type === 'faq_accordion' && (
              <div className="space-y-4">
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Eyebrow</span>
                  <input
                    type="text"
                    value={selectedNode.content?.eyebrow || ''}
                    onChange={(e) => updateProp('content', 'eyebrow', e.target.value)}
                    className="w-full border rounded-lg p-2"
                    placeholder="HELP & SUPPORT"
                  />
                </label>
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Section Title</span>
                  <input
                    type="text"
                    value={selectedNode.content?.title || ''}
                    onChange={(e) => updateProp('content', 'title', e.target.value)}
                    className="w-full border rounded-lg p-2"
                    placeholder="Frequently Asked Questions"
                  />
                </label>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-xs text-slate-700">Questions ({Array.isArray(selectedNode.content?.items) ? selectedNode.content.items.length : 0})</span>
                    <button
                      type="button"
                      onClick={() => {
                        const current = Array.isArray(selectedNode.content?.items) ? [...selectedNode.content.items] : [];
                        current.push({
                          q: 'New Question?',
                          a: 'Answer explanation goes here.',
                        });
                        updateProp('content', 'items', current);
                      }}
                      className="text-xs font-bold text-[#2b3380] hover:underline flex items-center gap-1"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Question
                    </button>
                  </div>
                  {(Array.isArray(selectedNode.content?.items) ? selectedNode.content.items : []).map((faq, qIdx) => (
                    <div key={qIdx} className="p-3 border rounded-xl bg-slate-50 space-y-2 relative">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold text-slate-500">Q#{qIdx + 1}</span>
                        <button
                          type="button"
                          onClick={() => {
                            const current = [...selectedNode.content.items];
                            current.splice(qIdx, 1);
                            updateProp('content', 'items', current);
                          }}
                          className="text-red-500 hover:text-red-700 p-1"
                          title="Remove FAQ"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-slate-600 block mb-1">Question</span>
                        <input
                          type="text"
                          value={faq.q || ''}
                          onChange={(e) => {
                            const current = [...selectedNode.content.items];
                            current[qIdx] = { ...current[qIdx], q: e.target.value };
                            updateProp('content', 'items', current);
                          }}
                          className="w-full border rounded-lg p-1.5 text-xs bg-white"
                        />
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-slate-600 block mb-1">Answer</span>
                        <textarea
                          rows={2}
                          value={faq.a || ''}
                          onChange={(e) => {
                            const current = [...selectedNode.content.items];
                            current[qIdx] = { ...current[qIdx], a: e.target.value };
                            updateProp('content', 'items', current);
                          }}
                          className="w-full border rounded-lg p-1.5 text-xs bg-white"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ============================================================ */}
        {/* 2. STYLE TAB                                                */}
        {/* ============================================================ */}
        {activeTab === 'style' && (
          <div className="space-y-4">
            {/* Header Style */}
            {isHeader && (
              <div className="space-y-4">
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Header Background Color</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={selectedNode.content?.background_color || '#2B3380'}
                      onChange={(e) => updateProp('content', 'background_color', e.target.value)}
                      className="w-8 h-8 rounded border border-slate-200 p-0 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={selectedNode.content?.background_color || ''}
                      onChange={(e) => updateProp('content', 'background_color', e.target.value)}
                      placeholder="#2B3380"
                      className="flex-1 border rounded-lg p-2 font-mono text-[11px]"
                    />
                  </div>
                </label>

                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Text & Navigation Links Color</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={selectedNode.content?.text_color || '#FFFFFF'}
                      onChange={(e) => updateProp('content', 'text_color', e.target.value)}
                      className="w-8 h-8 rounded border border-slate-200 p-0 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={selectedNode.content?.text_color || ''}
                      onChange={(e) => updateProp('content', 'text_color', e.target.value)}
                      placeholder="#FFFFFF"
                      className="flex-1 border rounded-lg p-2 font-mono text-[11px]"
                    />
                  </div>
                </label>

                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="font-bold text-slate-700 block mb-1">Vertical Padding</span>
                    <input
                      type="text"
                      value={selectedNode.content?.padding_y || '14px'}
                      onChange={(e) => updateProp('content', 'padding_y', e.target.value)}
                      className="w-full border rounded-lg p-2 font-mono text-xs"
                      placeholder="14px"
                    />
                  </label>
                  <label className="block">
                    <span className="font-bold text-slate-700 block mb-1">Horizontal Padding</span>
                    <input
                      type="text"
                      value={selectedNode.content?.padding_x || '24px'}
                      onChange={(e) => updateProp('content', 'padding_x', e.target.value)}
                      className="w-full border rounded-lg p-2 font-mono text-xs"
                      placeholder="24px"
                    />
                  </label>
                </div>
              </div>
            )}

            {/* Footer Style */}
            {isFooter && (
              <div className="space-y-4">
                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Footer Background Color</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={selectedNode.content?.background_color || '#FFFFFF'}
                      onChange={(e) => updateProp('content', 'background_color', e.target.value)}
                      className="w-8 h-8 rounded border border-slate-200 p-0 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={selectedNode.content?.background_color || ''}
                      onChange={(e) => updateProp('content', 'background_color', e.target.value)}
                      placeholder="#FFFFFF"
                      className="flex-1 border rounded-lg p-2 font-mono text-[11px]"
                    />
                  </div>
                </label>

                <label className="block">
                  <span className="font-bold text-slate-700 block mb-1">Footer Text Color</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={selectedNode.content?.text_color || '#334155'}
                      onChange={(e) => updateProp('content', 'text_color', e.target.value)}
                      className="w-8 h-8 rounded border border-slate-200 p-0 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={selectedNode.content?.text_color || ''}
                      onChange={(e) => updateProp('content', 'text_color', e.target.value)}
                      placeholder="#334155"
                      className="flex-1 border rounded-lg p-2 font-mono text-[11px]"
                    />
                  </div>
                </label>

                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="font-bold text-slate-700 block mb-1">Top Padding</span>
                    <input
                      type="text"
                      value={selectedNode.content?.padding_top || '48px'}
                      onChange={(e) => updateProp('content', 'padding_top', e.target.value)}
                      className="w-full border rounded-lg p-2 font-mono text-xs"
                      placeholder="48px"
                    />
                  </label>
                  <label className="block">
                    <span className="font-bold text-slate-700 block mb-1">Bottom Padding</span>
                    <input
                      type="text"
                      value={selectedNode.content?.padding_bottom || '32px'}
                      onChange={(e) => updateProp('content', 'padding_bottom', e.target.value)}
                      className="w-full border rounded-lg p-2 font-mono text-xs"
                      placeholder="32px"
                    />
                  </label>
                </div>
              </div>
            )}

            {!isHeader && !isFooter && (
              <>
            {/* Background Color */}
            <label className="block">
              <span className="font-bold text-slate-700 block mb-1">Background Color</span>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={selectedNode.style?.backgroundColor || '#ffffff'}
                  onChange={(e) => updateProp('style', 'backgroundColor', e.target.value)}
                  className="w-8 h-8 rounded border border-slate-200 p-0 cursor-pointer"
                />
                <input
                  type="text"
                  value={selectedNode.style?.backgroundColor || ''}
                  onChange={(e) => updateProp('style', 'backgroundColor', e.target.value)}
                  placeholder="#ffffff or transparent"
                  className="flex-1 border rounded-lg p-2 font-mono text-[11px]"
                />
              </div>
            </label>

            {/* Text Color (for text/heading) */}
            {['heading', 'text', 'button', 'page_hero'].includes(selectedNode.type) && (
              <label className="block">
                <span className="font-bold text-slate-700 block mb-1">Text Color</span>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={selectedNode.style?.color || '#1e293b'}
                    onChange={(e) => updateProp('style', 'color', e.target.value)}
                    className="w-8 h-8 rounded border border-slate-200 p-0 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={selectedNode.style?.color || ''}
                    onChange={(e) => updateProp('style', 'color', e.target.value)}
                    placeholder="#1e293b"
                    className="flex-1 border rounded-lg p-2 font-mono text-[11px]"
                  />
                </div>
              </label>
            )}

            {/* Text Alignment */}
            {['heading', 'text', 'button'].includes(selectedNode.type) && (
              <label className="block">
                <span className="font-bold text-slate-700 block mb-1">Alignment</span>
                <div className="grid grid-cols-3 gap-1 bg-slate-100 p-1 rounded-lg">
                  {['left', 'center', 'right'].map((align) => (
                    <button
                      key={align}
                      type="button"
                      onClick={() => updateProp('style', 'alignment', align)}
                      className={`py-1 capitalize rounded text-xs font-semibold ${
                        (selectedNode.style?.alignment || 'left') === align
                          ? 'bg-white text-[#2b3380] shadow-xs'
                          : 'text-slate-600'
                      }`}
                    >
                      {align}
                    </button>
                  ))}
                </div>
              </label>
            )}

            {/* Spacing (Padding Top / Bottom) */}
            <div className="border-t pt-3">
              <span className="font-bold text-slate-700 block mb-2">Padding (Spacing)</span>
              <div className="grid grid-cols-2 gap-2">
                <label>
                  <span className="text-[10px] text-slate-500 block mb-0.5">Top Padding</span>
                  <input
                    type="text"
                    value={selectedNode.style?.paddingTop || ''}
                    onChange={(e) => updateProp('style', 'paddingTop', e.target.value)}
                    placeholder="e.g. 40px"
                    className="w-full border rounded-lg p-1.5 font-mono text-[11px]"
                  />
                </label>
                <label>
                  <span className="text-[10px] text-slate-500 block mb-0.5">Bottom Padding</span>
                  <input
                    type="text"
                    value={selectedNode.style?.paddingBottom || ''}
                    onChange={(e) => updateProp('style', 'paddingBottom', e.target.value)}
                    placeholder="e.g. 40px"
                    className="w-full border rounded-lg p-1.5 font-mono text-[11px]"
                  />
                </label>
              </div>
            </div>

            {/* Border Radius */}
            <div className="border-t pt-3">
              <label className="block">
                <span className="font-bold text-slate-700 block mb-1">Corner Radius (Border Radius)</span>
                <input
                  type="text"
                  value={selectedNode.style?.borderRadius || ''}
                  onChange={(e) => updateProp('style', 'borderRadius', e.target.value)}
                  placeholder="e.g. 12px"
                  className="w-full border rounded-lg p-2 font-mono text-[11px]"
                />
              </label>
            </div>
            </>
            )}
          </div>
        )}

        {/* ============================================================ */}
        {/* 3. ADVANCED TAB                                             */}
        {/* ============================================================ */}
        {activeTab === 'advanced' && (
          <div className="space-y-4">
            {isHeader && (
              <div className="space-y-4">
                <div className="border rounded-xl p-3 bg-slate-50 space-y-2">
                  <span className="font-bold text-slate-800 block text-xs">Header Components Visibility</span>
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-xs text-slate-700">Search Bar</span>
                    <input
                      type="checkbox"
                      checked={selectedNode.content?.show_search !== false}
                      onChange={(e) => updateProp('content', 'show_search', e.target.checked)}
                      className="rounded text-[#2b3380]"
                    />
                  </label>
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-xs text-slate-700">Support CTA Button</span>
                    <input
                      type="checkbox"
                      checked={selectedNode.content?.show_support !== false}
                      onChange={(e) => updateProp('content', 'show_support', e.target.checked)}
                      className="rounded text-[#2b3380]"
                    />
                  </label>
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-xs text-slate-700">Action Icons (Wishlist, Cart, User)</span>
                    <input
                      type="checkbox"
                      checked={selectedNode.content?.show_actions !== false}
                      onChange={(e) => updateProp('content', 'show_actions', e.target.checked)}
                      className="rounded text-[#2b3380]"
                    />
                  </label>
                </div>
              </div>
            )}

            {isFooter && (
              <div className="space-y-4">
                <div className="border rounded-xl p-3 bg-slate-50 space-y-2">
                  <span className="font-bold text-slate-800 block text-xs">Footer Sections Visibility</span>
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-xs text-slate-700">Social Media Links</span>
                    <input
                      type="checkbox"
                      checked={selectedNode.content?.show_social !== false}
                      onChange={(e) => updateProp('content', 'show_social', e.target.checked)}
                      className="rounded text-[#2b3380]"
                    />
                  </label>
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-xs text-slate-700">Action Buttons</span>
                    <input
                      type="checkbox"
                      checked={selectedNode.content?.show_actions !== false}
                      onChange={(e) => updateProp('content', 'show_actions', e.target.checked)}
                      className="rounded text-[#2b3380]"
                    />
                  </label>
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-xs text-slate-700">Office Locations</span>
                    <input
                      type="checkbox"
                      checked={selectedNode.content?.show_offices !== false}
                      onChange={(e) => updateProp('content', 'show_offices', e.target.checked)}
                      className="rounded text-[#2b3380]"
                    />
                  </label>
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-xs text-slate-700">Security Notice Disclaimer</span>
                    <input
                      type="checkbox"
                      checked={selectedNode.content?.show_disclaimer !== false}
                      onChange={(e) => updateProp('content', 'show_disclaimer', e.target.checked)}
                      className="rounded text-[#2b3380]"
                    />
                  </label>
                </div>
              </div>
            )}

            {!isHeader && !isFooter && (
              <>
            {/* Responsive Visibility */}
            <div>
              <span className="font-bold text-slate-700 block mb-1.5">Responsive Visibility</span>
              <p className="text-[10px] text-slate-400 mb-2">
                Control whether this element displays on specific device screens.
              </p>
              <div className="space-y-2 bg-slate-50 p-3 rounded-xl border border-slate-200">
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="font-medium text-slate-700">Display on Desktop</span>
                  <input
                    type="checkbox"
                    checked={selectedNode.visibility?.desktop !== false}
                    onChange={(e) => updateVisibility('desktop', e.target.checked)}
                    className="rounded text-[#2b3380]"
                  />
                </label>
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="font-medium text-slate-700">Display on Tablet</span>
                  <input
                    type="checkbox"
                    checked={selectedNode.visibility?.tablet !== false}
                    onChange={(e) => updateVisibility('tablet', e.target.checked)}
                    className="rounded text-[#2b3380]"
                  />
                </label>
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="font-medium text-slate-700">Display on Mobile</span>
                  <input
                    type="checkbox"
                    checked={selectedNode.visibility?.mobile !== false}
                    onChange={(e) => updateVisibility('mobile', e.target.checked)}
                    className="rounded text-[#2b3380]"
                  />
                </label>
              </div>
            </div>

            {/* Semantic Anchor ID */}
            <div>
              <label className="block">
                <span className="font-bold text-slate-700 block mb-1">CSS ID / Anchor Tag</span>
                <input
                  type="text"
                  value={selectedNode.anchorId || ''}
                  onChange={(e) => updateRootProp('anchorId', e.target.value.replace(/[^a-zA-Z0-9-_]/g, ''))}
                  placeholder="e.g. device-section (for #anchor links)"
                  className="w-full border rounded-lg p-2 font-mono text-[11px]"
                />
              </label>
            </div>

            {/* Actions */}
            <div className="border-t pt-4 space-y-2">
              <button
                type="button"
                onClick={() => onDuplicateNode(selectedNode.id)}
                className="w-full py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold flex items-center justify-center gap-1.5 transition-colors"
              >
                <Copy className="w-3.5 h-3.5" />
                <span>Duplicate Element</span>
              </button>
              <button
                type="button"
                onClick={() => onDeleteNode(selectedNode.id)}
                className="w-full py-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-xl font-bold flex items-center justify-center gap-1.5 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Delete Element</span>
              </button>
            </div>
            </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
